import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import '../../common.css';
import { getAccessToken } from "../../services/authApi";

const AI_FUNCTIONAPP_API_URL = process.env.REACT_APP_AI_FUNCTIONAPP_API_URL;
const AI_FUNCTIONAPP_API_KEY = process.env.REACT_APP_AI_FUNCTIONAPP_API_KEY;

const ChatConversation = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Scroll to bottom when messages update
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const mockResponses = [
        {
            input: "Hello",
            result: "Hi! How can I assist you today?"
        },
        {
            input: "What is the interest rate?",
            result: "Interest rates vary based on loan type and market conditions. Please specify your loan type."
        },
        {
            input: "Tell me about loan options",
            result: "We offer fixed-rate, adjustable-rate, and interest-only loans. Would you like details on any specific type?"
        },
        {
            input: "How do I upload documents?",
            result: "You can upload documents via the Documents section in the sidebar menu."
        },
        {
            input: "What is my loan status?",
            result: "Your loan is currently in underwriting. You will be notified once a decision is made."
        },
        {
            input: "Can I change my loan amount?",
            result: "Yes, you can request a change in loan amount by submitting a Change of Circumstance form."
        },
        {
            input: "Who is my loan officer?",
            result: "Your loan officer is Jane Doe. You can contact her at jane.doe@company.com."
        },
        {
            input: "What documents are required?",
            result: "Required documents include income proof, identification, and property details."
        },
        {
            input: "How do I lock my rate?",
            result: "You can lock your rate in the Pricing section. Please contact your loan officer for assistance."
        },
        {
            input: "Thank you",
            result: "You're welcome! Let me know if you have any other questions."
        }
    ];

    const sendMessage = async () => {
        if (!input.trim()) return;
        const userMessage = { role: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setLoading(true);
        try {
            const payload = {
                message: input,
                session_id: "abc123-session",
                user_id: "user42"
            };
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            //const token = await getAccessToken(authCode);

            const response = await fetch(`${AI_FUNCTIONAPP_API_URL}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': AI_FUNCTIONAPP_API_KEY,
                    'Authorization': `Bearer ${authCode}`
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            setMessages(prev => [...prev, { role: 'ai', text: data.response || "No response." }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'ai', text: "Error: Unable to get response." }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md flex flex-col relative min-h-screen" style={{ position: 'relative', minHeight: '100vh' }}>
            <div className="panel-heading font-bold p-4">Guideline Pro - AI Chat Assistant</div>
            {/* Conversation panel with scroll */}
            <div
                className="flex-1 p-4 space-y-3"
                style={{
                    overflowY: 'auto',
                    border: 'none',
                    marginBottom: 0,
                    paddingBottom: '16px'
                }}
            >
                {messages.length === 0 && (
                    <div className="text-gray-400 text-center mt-10">Start a conversation with AI...</div>
                )}
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] p-3 rounded-lg shadow text-sm ${msg.role === 'user' ? 'bg-[#2080C3] text-white' : 'bg-gray-100 text-gray-800'}`}>
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
            {/* Input panel fixed at bottom */}
            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    background: 'white',
                    borderTop: '1px solid #e5e7eb',
                    zIndex: 20
                }}
                className="p-4 flex items-center"
            >
                <textarea
                    ref={inputRef}
                    className="form-input flex-1 resize-none p-2 rounded-md border mr-2"
                    rows={2}
                    placeholder="Ask anything about guidelines..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    maxLength={500}
                    style={{ minHeight: '44px' }}
                />
                <button
                    className="btn-custom rounded text-[#FFFFFF] bg-[#2080C3] hover:text-[#E6EEF8] hover:bg-[#5DA5D9] active:bg-white transition-colors duration-200 px-4 py-2 font-semibold"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    style={{ minHeight: '44px' }}
                >
                    {loading ? 'Processing...' : 'Send'}
                </button>
            </div>
            {/* Spacer to prevent content being hidden behind input panel */}
            <div style={{ height: '80px' }} />
        </div>
    );
};

export default ChatConversation;
